<template>
  <AModal modal-class="d-upvotes-modal modal-sm">
    <div
      class="overlay"
      @click="dismiss"
    />

    <div class="modal-content">
      <div class="modal-header">
        <h4>Who likes this</h4>
        <a
          class="button button-yellow button-icon button-close-white button-close-modal"
          @click="dismiss"
        />
      </div>

      <div class="modal-body">
        <div
          v-if="upvotes.length"
          class="upvotes-container"
        >
          <DScrollableContent>
            <template slot="content">
              <DUserFollowPanel
                v-for="upvoteUser in upvotes"
                :key="upvoteUser.id"
                :is-followed="!!upvoteUser.is_followed"
                :is-user-signed-in="isUserSignedIn"
                :user="upvoteUser"
              />

              <div
                id="load-more-upvotes"
                class="load-more-upvotes"
              />
            </template>
          </DScrollableContent>
        </div>

        <div
          v-if="isLoading"
          class="loading-container"
        >
          <DuiLoadingIndicator size="lg" />
        </div>
      </div>
    </div>
  </AModal>
</template>

<script>
import Vue from 'vue';

import AModal from '@d-components/a-modal/a-modal.vue';
import activityFeedService from '@d-services/activity-feed-service/activity-feed-service';
import commentsService from '@d-services/comments-service/comments-service';
import DScrollableContent from '@d-components/d-scrollable-content/d-scrollable-content.vue';
import DUserFollowPanel from '@d-components/d-user-follow-panel/d-user-follow-panel.vue';
import { getErrorMessage } from '@d-shared/utils/errors';
import userReviewsService from '@d-services/user-reviews-service/user-reviews-service';
import { whenElementIsVisible } from '@dui/helpers/util-funcs';

export default {
  components: {
    AModal,
    DScrollableContent,
    DUserFollowPanel,
  },
  props: {
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book-comment',
          'post',
          'post-comment',
          'user-review',
          'user-review-comment',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
    parentSlug: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    disableLoadMore: null,
    isLoading: false,
    paginationInfo: {
      hasMoreResults: false,
      pageNum: 0,
    },
    upvotes: [],
  }),
  beforeDestroy() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  },
  mounted() {
    this.loadUpvotes();
  },
  methods: {
    dismiss() {
      this.$emit('dismiss');
    },

    async enableLoadMore() {
      if (this.disableLoadMore) {
        this.disableLoadMore();
      }

      // load-more-upvotes element is conditionally rendered
      // according to the existence of results
      await this.$nextTick();
      const relativeToEl = this.$el.querySelector('.d-scrollable-content .d-scrollable-items');
      const disableLoadMore = whenElementIsVisible('#load-more-upvotes', this.loadUpvotes.bind(this), relativeToEl);
      this.$set(this, 'disableLoadMore', disableLoadMore);
    },

    async loadUpvotes() {
      this.$set(this, 'isLoading', true);

      try {
        this.paginationInfo.pageNum++;

        let upvotesResult;

        switch(this.itemType) {
          case 'book-comment':
            upvotesResult = await commentsService.getBookCommentUpvotes(this.parentSlug, this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'post':
            upvotesResult = await activityFeedService.getPostUpvotes(this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'post-comment':
            upvotesResult = await commentsService.getPostCommentUpvotes(this.parentSlug, this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'user-review':
            upvotesResult = await userReviewsService.getUserReviewUpvotes(this.itemSlug, this.paginationInfo.pageNum);
            break;
          case 'user-review-comment':
            upvotesResult = await commentsService.getUserReviewCommentUpvotes(this.parentSlug, this.itemSlug, this.paginationInfo.pageNum);
            break;
        }

        this.$set(this, 'upvotes', this.upvotes.concat(upvotesResult.upvotes));
        this.$set(this.paginationInfo, 'hasMoreResults', !!upvotesResult.meta.next_page);

        if (this.paginationInfo.hasMoreResults) {
          this.enableLoadMore();
        }
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-upvotes-modal {
  .upvotes-container {
    height: 70vh;
    position: relative;

    @include screenVerticalMoreThan(md) {
      height: 500px;
    }
  }

  .modal-body {
    min-height: $_7em;
    padding: 0 $_2em $_2em;
  }

  .loading-container {
    bottom: $_2em;
    left: 50%;
    position: absolute;
    text-align: center;
    transform: translateX(-50%);
    z-index: 1;
  }
}
</style>
